
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Separator } from '@/components/ui/separator';
import { SECTIONS, VALIDATION_SECTIONS } from '@/constants/application';
import { CheckCircle, AlertCircle, Loader2 } from 'lucide-react';
import type { Application } from '@/types/database.types';
import { hasRequiredFields } from '@/utils/applicationValidation';
import { useState, useEffect } from 'react';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { useToast } from '@/hooks/use-toast';
import { useNavigate } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';

interface ReviewAndSubmitProps {
  data: Partial<Application>;
  onSave: (data: Partial<Application>) => void;
}

export const ReviewAndSubmit = ({ data, onSave }: ReviewAndSubmitProps) => {
  const [acknowledgmentChecked, setAcknowledgmentChecked] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [hasSubmitted, setHasSubmitted] = useState(false);
  const { toast } = useToast();
  const navigate = useNavigate();

  // Add a safety timeout to reset the submitting state after 30 seconds if stuck
  useEffect(() => {
    let timeoutId: number | undefined;
    
    if (isSubmitting && !hasSubmitted) {
      timeoutId = window.setTimeout(() => {
        console.log('Submission timeout reached, resetting submit button state');
        setIsSubmitting(false);
        toast({
          title: "Submission Taking Too Long",
          description: "The submission is taking longer than expected. Please try again.",
          variant: "destructive",
        });
      }, 30000); // 30 second timeout
    }
    
    return () => {
      if (timeoutId) window.clearTimeout(timeoutId);
    };
  }, [isSubmitting, hasSubmitted, toast]);

  // Add effect to handle successful submission and navigation
  useEffect(() => {
    if (hasSubmitted && data.status === 'submitted') {
      console.log('Application submitted successfully, redirecting...');
      
      // Double-check application status in database to ensure it's actually updated
      const verifySubmission = async () => {
        if (!data.id) return;
        
        try {
          const { data: appData, error } = await supabase
            .from('applications')
            .select('status')
            .eq('id', data.id)
            .single();
            
          if (error) {
            console.error('Error verifying submission status:', error);
            return;
          }
          
          console.log('Verified application status from database:', appData.status);
          
          if (appData.status !== 'submitted') {
            console.warn('Application status mismatch! Database says:', appData.status);
            
            // Force update to submitted status if needed
            const { error: updateError } = await supabase
              .from('applications')
              .update({ status: 'submitted' })
              .eq('id', data.id);
              
            if (updateError) {
              console.error('Error forcing status update:', updateError);
            } else {
              console.log('Successfully forced application status to submitted');
            }
          }
        } catch (err) {
          console.error('Error in verification process:', err);
        }
      };
      
      // Run the verification
      verifySubmission();
      
      // Store submission flag in session storage to maintain state across refreshes
      sessionStorage.setItem('applicationSubmitted', 'true');
      sessionStorage.setItem('submittedApplicationId', data.id || '');
      
      // Show toast to indicate submission is processing
      toast({
        title: "Application Submitted",
        description: "Your application has been submitted successfully and is being processed.",
      });
      
      // Set a delay before redirecting to ensure the toast is visible
      const redirectTimeout = setTimeout(() => {
        navigate('/application/submitted');
      }, 1000);
      
      return () => clearTimeout(redirectTimeout);
    }
  }, [hasSubmitted, data.status, data.id, navigate, toast]);

  const handleSubmit = async () => {
    if (isSubmitting) return; // Prevent multiple clicks
    
    try {
      setIsSubmitting(true);
      
      // Log the submission attempt
      console.log('Submitting application with status update:', { 
        currentStatus: data.status,
        newStatus: 'submitted',
        applicationId: data.id
      });
      
      // Create a complete update object to ensure all required fields are sent
      const updateData = {
        ...data,
        status: 'submitted' as const,
      };
      
      // Save with submitted status
      await onSave(updateData);
      
      // Mark as submitted to trigger the redirect effect
      setHasSubmitted(true);
      
      // Note: The actual redirect happens in the useEffect above
    } catch (error) {
      console.error('Error submitting application:', error);
      toast({
        title: "Submission Failed",
        description: "There was a problem submitting your application. Please try again.",
        variant: "destructive",
      });
      setIsSubmitting(false);
    }
  };

  // We still calculate completeness to show status, but don't block submission
  const allSectionsComplete = SECTIONS.slice(0, -1).every(section => {
    // Handle special case for Payroll Schedule directly
    const validationSection = section === 'Payroll Schedule' ? 'Payroll Schedule' : 
                             VALIDATION_SECTIONS[section as keyof typeof VALIDATION_SECTIONS] || section;
    return hasRequiredFields(validationSection, data);
  });

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle>Application Review</CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          {SECTIONS.slice(0, -1).map((section, index) => {
            // Handle special case for Payroll Schedule directly
            const validationSection = section === 'Payroll Schedule' ? 'Payroll Schedule' : 
                                     VALIDATION_SECTIONS[section as keyof typeof VALIDATION_SECTIONS] || section;
            const isComplete = hasRequiredFields(validationSection, data);
            
            return (
              <div key={section}>
                <div className="flex items-center justify-between mb-2">
                  <h3 className="text-lg font-semibold flex items-center gap-2">
                    {section}
                    {isComplete ? (
                      <CheckCircle className="w-5 h-5 text-green-500" />
                    ) : (
                      <AlertCircle className="w-5 h-5 text-red-500" />
                    )}
                  </h3>
                  <span className={isComplete ? "text-green-600" : "text-red-600"}>
                    {isComplete ? "Complete" : "Incomplete"}
                  </span>
                </div>
                {index < SECTIONS.length - 2 && <Separator className="my-4" />}
              </div>
            );
          })}
        </CardContent>
      </Card>

      <Card>
        <CardContent className="pt-6">
          <div className="flex flex-col items-center justify-center space-y-4">
            <div className="flex items-center space-x-2 w-full mb-4 p-4 bg-slate-50 border border-primary/20 rounded-md">
              <div className="flex items-center space-x-3 w-full">
                <Switch
                  id="terms"
                  checked={acknowledgmentChecked}
                  onCheckedChange={setAcknowledgmentChecked}
                />
                <Label htmlFor="terms" className="text-sm text-gray-700 flex-1">
                  I acknowledge that submitting an incomplete or inaccurate application may result in no quotes, fewer options, or inaccurate PEO quotes and matching.
                </Label>
              </div>
            </div>

            <Button
              onClick={handleSubmit}
              disabled={!acknowledgmentChecked || isSubmitting || hasSubmitted}
              className="w-full md:w-auto relative"
            >
              {isSubmitting ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Submitting...
                </>
              ) : (
                "Submit Application"
              )}
            </Button>
            
            {!allSectionsComplete && acknowledgmentChecked && (
              <p className="text-sm text-primary">
                Some sections are incomplete. This may affect the quality of quotes you receive.
              </p>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};
